/**
 * useSpeechRecognition — Feb 2026
 * ================================
 *
 * Thin wrapper around the browser's Web Speech API. Owns:
 *   • lifecycle of the `SpeechRecognition` instance
 *   • start / stop control
 *   • permission preflight (navigator.mediaDevices.getUserMedia)
 *   • error surfaces (mic blocked, no device, network drop)
 *   • the brief "Listening — speak now" visual cue (~1.4s)
 *
 * Streams both interim and final transcripts through the caller's
 * `onTranscript` callback so the parent chat page can drive its
 * message input state exactly as before — no auto-send, the user
 * still explicitly hits Send.
 *
 * Extracted from `ConversationsChat.js` (Feb 2026 refactor). Zero
 * behaviour change: same permission prompts, same error copy, same
 * "clean stop on silence" semantics, same StrictMode-safe cleanup.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { devLog } from '../utils/devLogger';

const isBrowser = typeof window !== 'undefined';
const speechSupported =
  isBrowser &&
  ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

const PERMISSION_BLOCKED_MSG =
  'Microphone access was blocked. Click the lock icon in your browser address bar, allow microphone for hibff.com, then click the mic again. You can still type your message in the box below.';
const NO_DEVICE_MSG =
  'No microphone detected. Plug one in or type your message below.';
const GENERIC_MSG =
  'Could not access the microphone. Type your message in the box below.';
const UNSUPPORTED_MSG =
  'Voice input is not supported in this browser. Use Chrome, Edge, or Safari — or type your message in the box below.';
const START_FAILED_MSG =
  'Speech recognition couldn\'t start. Refresh the page or type your message below.';

export const useSpeechRecognition = ({ onTranscript, lang = 'en-US', suppressed = false } = {}) => {
  const [isListening, setIsListening] = useState(false);
  const [micError, setMicError] = useState(null);
  const [micJustOpened, setMicJustOpened] = useState(false);

  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const abortCountdownRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);
  // Feb 2026 — when the parent sets `suppressed=true` (e.g. AvA's TTS is
  // playing), we drop every transcript event until suppression ends. This
  // stops the browser's SpeechRecognition from capturing Rebecca / Maya's
  // own voice through the speakers and appending it to the teen's input.
  const suppressedRef = useRef(!!suppressed);
  const finalTranscriptRef = useRef('');

  // Keep the callback ref up-to-date so the one-time setup effect
  // below can call the LATEST parent callback without re-initialising
  // SpeechRecognition on every keystroke.
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);

  useEffect(() => { isListeningRef.current = isListening; }, [isListening]);

  // Mirror the `suppressed` prop into a ref so the one-time onresult
  // handler can read the latest value without a re-init. When suppression
  // engages we also flush the accumulated finalTranscript so any partial
  // phrase captured moments earlier doesn't append to the next real
  // transcript.
  useEffect(() => {
    const next = !!suppressed;
    if (next && !suppressedRef.current) {
      finalTranscriptRef.current = '';
    }
    suppressedRef.current = next;
  }, [suppressed]);

  // ── One-time SpeechRecognition wiring ────────────────────────────────
  useEffect(() => {
    if (!speechSupported) {
      console.warn('Speech recognition not supported');
      return undefined;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true;      // keep listening through pauses
    rec.interimResults = true;
    rec.lang = lang;
    recognitionRef.current = rec;

    rec.onstart = () => {
      devLog('🎤 Listening...');
      setIsListening(true);
      finalTranscriptRef.current = '';
    };

    rec.onresult = (event) => {
      // Feb 2026 — if the AvA is speaking (suppressed=true), drop the
      // event entirely. The mic UI stays lit but Rebecca / Maya's own
      // voice can no longer bleed back into the teen's input.
      if (suppressedRef.current) {
        finalTranscriptRef.current = '';
        return;
      }
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscriptRef.current += `${transcript} `;
        } else {
          interimTranscript += transcript;
        }
      }
      // Stream to the parent — parent decides whether to overwrite or
      // append its message input. Matches the original inline behaviour.
      const combined = finalTranscriptRef.current + interimTranscript;
      if (typeof onTranscriptRef.current === 'function') {
        onTranscriptRef.current(combined);
      }
    };

    rec.onerror = (event) => {
      if (event.error !== 'no-speech') {
        console.error('Speech error:', event.error);
      }
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setIsListening(false);
        window.alert('Microphone access was blocked by the browser. Click the lock icon in the address bar, allow microphone for this site, and try again.');
        return;
      }
      if (event.error === 'audio-capture') {
        setIsListening(false);
        window.alert('No microphone was detected. Please connect a microphone or switch to Text mode below.');
        return;
      }
      if (event.error === 'aborted' || event.error === 'network') {
        setIsListening(false);
      }
    };

    rec.onend = () => {
      devLog('🔚 Recognition ended');
      setIsListening(false);
    };

    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) { /* already stopped */ }
      }
    };
    // Verified mount-only setup: the SpeechRecognition instance is built
    // once when the hook mounts and configured with the `lang` prop as
    // its initial locale. Re-running this effect when `lang` changes
    // would tear down and rebuild the whole recognizer mid-conversation
    // (losing any in-flight utterance). Callers that need to switch
    // language should remount the hook with a `key` prop change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── start() ──────────────────────────────────────────────────────────
  const start = useCallback(async () => {
    setMicError(null);
    if (!recognitionRef.current) {
      setMicError(UNSUPPORTED_MSG);
      return;
    }
    abortCountdownRef.current = false;

    // Permission preflight — non-blocking. The main SpeechRecognition
    // call fires in parallel so the WebRTC stream begins immediately.
    (async () => {
      try {
        if (navigator.mediaDevices?.getUserMedia) {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop());
        }
      } catch (err) {
        console.error('Microphone permission error:', err);
        const errMsg =
          err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError'
            ? PERMISSION_BLOCKED_MSG
            : err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError'
              ? NO_DEVICE_MSG
              : GENERIC_MSG;
        setMicError(errMsg);
        setIsListening(false);
        try { recognitionRef.current?.stop(); } catch (e) { /* noop */ }
      }
    })();

    setIsListening(true);
    try {
      recognitionRef.current.start();
      setMicJustOpened(true);
      setTimeout(() => setMicJustOpened(false), 1400);
    } catch (error) {
      if (!String(error?.message || '').includes('already started')) {
        console.error('Mic error:', error);
        setMicError(START_FAILED_MSG);
        setIsListening(false);
      }
    }
  }, []);

  // ── stop() ───────────────────────────────────────────────────────────
  const stop = useCallback(() => {
    abortCountdownRef.current = true;
    setIsListening(false);
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) { /* noop */ }
    }
  }, []);

  return {
    isListening,
    micError,
    micJustOpened,
    speechSupported,
    setMicError,
    start,
    stop,
    recognitionRef,     // exposed for legacy callers that still need direct control
    isListeningRef,     // exposed so unmount cleanup can inspect without re-render
  };
};

export default useSpeechRecognition;
